/* global axios */

const baseUrl = accountId => `/api/v1/accounts/${accountId}/lilith/team`;
const assistantsUrl = accountId =>
  `/api/v1/accounts/${accountId}/lilith/assistants`;
const getTimezoneOffset = () => -new Date().getTimezoneOffset() / 60;

export const getLilithAssistants = accountId =>
  axios.get(assistantsUrl(accountId));

export const createLilithAssistant = (accountId, data) =>
  axios.post(assistantsUrl(accountId), {
    assistant: data,
  });

export const updateLilithAssistant = (accountId, assistantId, data) =>
  axios.patch(`${assistantsUrl(accountId)}/${assistantId}`, {
    assistant: data,
  });

export const deleteLilithAssistant = (accountId, assistantId) =>
  axios.delete(`${assistantsUrl(accountId)}/${assistantId}`);

export const getLilithAssistantInboxes = (accountId, assistantId, signal) =>
  axios.get(`${assistantsUrl(accountId)}/${assistantId}/inboxes`, {
    ...(signal ? { signal } : {}),
  });

export const connectLilithAssistantInbox = (accountId, assistantId, inboxId) =>
  axios.post(`${assistantsUrl(accountId)}/${assistantId}/inboxes`, {
    inbox: { inbox_id: inboxId },
  });

export const disconnectLilithAssistantInbox = (
  accountId,
  assistantId,
  inboxId
) =>
  axios.delete(`${assistantsUrl(accountId)}/${assistantId}/inboxes/${inboxId}`);

export const sendLilithPlaygroundMessage = (
  accountId,
  assistantId,
  messageContent,
  messageHistory,
  playgroundConfig
) =>
  axios.post(`${assistantsUrl(accountId)}/${assistantId}/playground`, {
    assistant: {
      message_content: messageContent,
      message_history: messageHistory,
      playground_config: playgroundConfig,
    },
  });

export const getLilithAssistantMetrics = (
  accountId,
  assistantId,
  range,
  signal
) =>
  axios.get(`${assistantsUrl(accountId)}/${assistantId}/metrics`, {
    params: { range, timezone_offset: getTimezoneOffset() },
    ...(signal ? { signal } : {}),
  });

export const getLilithAssistantFaqStats = (accountId, assistantId, signal) =>
  axios.get(`${assistantsUrl(accountId)}/${assistantId}/faq_stats`, {
    ...(signal ? { signal } : {}),
  });

export const getLilithAssistantSummary = (
  accountId,
  assistantId,
  range,
  snapshotAt,
  signal
) =>
  axios.get(`${assistantsUrl(accountId)}/${assistantId}/summary`, {
    ...(signal ? { signal } : {}),
    params: {
      snapshot_at: snapshotAt,
      range,
      timezone_offset: getTimezoneOffset(),
    },
  });

export const getLilithAssistantDrilldown = (
  accountId,
  assistantId,
  metric,
  range,
  page,
  signal,
  snapshotAt
) =>
  axios.get(`${assistantsUrl(accountId)}/${assistantId}/drilldown`, {
    params: {
      metric,
      range,
      snapshot_at: snapshotAt,
      timezone_offset: getTimezoneOffset(),
      page,
    },
    ...(signal ? { signal } : {}),
  });

export const getTeamThreads = (accountId, page = 1) =>
  axios.get(`${baseUrl(accountId)}/threads`, {
    params: { page },
  });

export const getTeamThread = (accountId, threadRef) =>
  axios.get(`${baseUrl(accountId)}/thread`, {
    params: { thread_ref: threadRef },
  });

export const sendTeamMessage = (
  accountId,
  conversationId,
  message,
  requestType = 'chat',
  threadRef = null,
  assistantRef = null,
  requestRef = crypto.randomUUID()
) =>
  axios.post(`${baseUrl(accountId)}/message`, {
    ...(conversationId ? { conversation_id: conversationId } : {}),
    message,
    request_type: requestType,
    request_ref: requestRef,
    ...(threadRef ? { thread_ref: threadRef } : {}),
    ...(assistantRef ? { assistant_ref: assistantRef } : {}),
  });

async function sendComposerRequest(
  accountId,
  conversationId,
  endpoint,
  input,
  signal,
  employeeId
) {
  const identity = JSON.stringify([
    accountId,
    employeeId,
    conversationId || null,
    endpoint,
    input,
  ]);
  const digest = Array.from(
    new Uint8Array(
      await crypto.subtle.digest('SHA-256', new TextEncoder().encode(identity))
    )
  )
    .map(byte => byte.toString(16).padStart(2, '0'))
    .join('');
  const storageKey = `lilith-composer:${digest}`;
  let requestRef = sessionStorage.getItem(storageKey);
  if (
    !/^[0-9a-f]{8}-(?:[0-9a-f]{4}-){3}[0-9a-f]{12}$/i.test(requestRef || '')
  ) {
    requestRef = crypto.randomUUID();
    sessionStorage.setItem(storageKey, requestRef);
  }
  try {
    const response = await axios.post(
      `${baseUrl(accountId)}/${endpoint}`,
      {
        ...(conversationId ? { conversation_id: conversationId } : {}),
        ...input,
        request_ref: requestRef,
      },
      { signal }
    );
    if (!response.data?.failed || !response.data?.retryable)
      sessionStorage.removeItem(storageKey);
    return response;
  } catch (error) {
    if (error?.response?.data?.discarded) sessionStorage.removeItem(storageKey);
    throw error;
  }
}

export const sendTeamComposerAction = (
  accountId,
  conversationId,
  action,
  content,
  signal,
  employeeId
) =>
  sendComposerRequest(
    accountId,
    conversationId,
    'composer_action',
    {
      operation: action,
      content,
    },
    signal,
    employeeId
  );

export const sendTeamComposerFollowUp = (
  accountId,
  conversationId,
  followUpContext,
  message,
  signal,
  employeeId
) =>
  sendComposerRequest(
    accountId,
    conversationId,
    'composer_follow_up',
    {
      follow_up_context: followUpContext,
      message,
    },
    signal,
    employeeId
  );

const scenariosUrl = (accountId, assistantId) =>
  `${assistantsUrl(accountId)}/${assistantId}/scenarios`;

export const getLilithScenarios = (
  accountId,
  assistantId,
  includeDisabled = false
) =>
  axios.get(scenariosUrl(accountId, assistantId), {
    params: { include_disabled: includeDisabled },
  });
export const createLilithScenario = (accountId, assistantId, scenario) =>
  axios.post(scenariosUrl(accountId, assistantId), { scenario });
export const updateLilithScenario = (accountId, assistantId, id, scenario) =>
  axios.patch(`${scenariosUrl(accountId, assistantId)}/${id}`, { scenario });
export const deleteLilithScenario = (accountId, assistantId, id) =>
  axios.delete(`${scenariosUrl(accountId, assistantId)}/${id}`);
